import { subscriptionTypes } from '@constants/index';
import textHelper from '@utils/textHelper';

export type PlanTagType = number | string | undefined;

interface TaxBreakdownItem {
  amount: number;
  label: string;
}

export interface BundleSubscription {
  uid: string;
  type?: number;
  tax_breakdown?: TaxBreakdownItem[];
  bundle_saved_amount?: number;
  [key: string]: unknown;
}

export interface BundleAddOn {
  uid: string;
  name: string;
  add_for: number;
  originally_for: number;
  is_referral_invalid?: boolean;
  topology_details?: Array<{
    uid: string;
    type: string;
    name: string;
    title?: string;
  }>;
  subscription?: BundleSubscription;
  isMandatory?: boolean;
  mandatoryBundles?: BundleAddOn[];
  [key: string]: unknown;
}

export const isIconicProSubscription = (
  subscription?: { type?: number } | null
) => subscription?.type === subscriptionTypes.ICONIC_PRO;

/**
 * Combo UI gate:
 * - Iconic Pro → CMS topology_details (empty skips combo)
 * - Plus / Iconic / Lite → existing bundle_add_ons length
 */
export const shouldShowBundleAddOns = ({
  isIconicPro,
  cmsHasComboTopologies = false,
  bundleAddOnsLength = 0
}: {
  isIconicPro: boolean;
  cmsHasComboTopologies?: boolean;
  bundleAddOnsLength?: number;
}) => {
  if (isIconicPro) {
    return cmsHasComboTopologies;
  }
  return bundleAddOnsLength > 0;
};

export type CmsTopologyDetail = {
  uid: string;
  type: string;
  name: string;
  title?: string;
};

export interface ComboTopology {
  type?: number | string;
  name?: string;
  uid?: string;
}

const isPlusTopologyType = (type?: ComboTopology['type']) =>
  type === subscriptionTypes.PLUS || type === 'Plus';

const isIconicProTopologyType = (type?: ComboTopology['type']) =>
  type === subscriptionTypes.ICONIC_PRO || type === 'Iconic Pro';

const isIconicTopologyType = (type?: ComboTopology['type']) =>
  type === subscriptionTypes.ICONIC || type === 'Iconic';

export const getComboGoalsByTier = (topologies: ComboTopology[] = []) => {
  let plusGoals = '';
  let iconicGoals = '';
  let iconicProGoals = '';

  topologies.forEach(({ type, name = '' }) => {
    if (isPlusTopologyType(type)) {
      plusGoals += `${plusGoals ? ' + ' : ''}${name}`;
    } else if (isIconicProTopologyType(type)) {
      iconicProGoals += `${iconicProGoals ? ' + ' : ''}${name}`;
    } else if (isIconicTopologyType(type)) {
      iconicGoals += `${iconicGoals ? ' + ' : ''}${name}`;
    }
  });

  return {
    plusGoals,
    iconicGoals,
    iconicProGoals
  };
};

const getPlanTypeLabel = (type?: ComboTopology['type']) => {
  if (type === subscriptionTypes.ICONIC_PRO || type === 'Iconic Pro') {
    return 'Iconic Pro';
  }
  if (type === subscriptionTypes.ICONIC || type === 'Iconic') {
    return 'Iconic';
  }
  if (type === subscriptionTypes.PLUS || type === 'Plus') {
    return 'Plus';
  }
  if (type === subscriptionTypes.LITE || type === 'Lite') {
    return 'Lite';
  }
  return typeof type === 'string' ? type : '';
};

export type MwebBundleSummaryItem = {
  key: string;
  title: string;
  displayDuration?: string;
  endsAt?: string | Date | null;
  taxBreakdown?: TaxBreakdownItem[] | null;
  showPrice: boolean;
};

/**
 * Mweb payment upper-section line items.
 * Real combos use childSubscriptions; Iconic Pro CMS uses topologies
 * (no childSubscriptions on the primary order).
 */
export const getMwebBundleSummaryItems = ({
  subscription,
  topologies = [],
  fallbackDuration = ''
}: {
  subscription?: {
    displayDuration?: string;
    childSubscriptions?: Array<{
      uid?: string;
      title?: string;
      displayDuration?: string;
      checkoutDatesWithBenefits?: { endsAt?: string };
      taxBreakdown?: TaxBreakdownItem[];
    }>;
    checkoutDatesWithBenefits?: { endsAt?: string };
    taxBreakdown?: TaxBreakdownItem[];
    tax_breakdown?: TaxBreakdownItem[];
  } | null;
  topologies?: ComboTopology[];
  fallbackDuration?: string;
}): MwebBundleSummaryItem[] => {
  const childSubscriptions = subscription?.childSubscriptions || [];
  if (childSubscriptions.length) {
    return childSubscriptions.map((child, index) => ({
      key: child.uid || `child-${index}`,
      title: child.title || '',
      displayDuration: child.displayDuration,
      endsAt: child.checkoutDatesWithBenefits?.endsAt,
      taxBreakdown: child.taxBreakdown,
      showPrice: true
    }));
  }

  if (!topologies.length) return [];

  const displayDuration =
    subscription?.displayDuration || fallbackDuration || '';
  const endsAt = subscription?.checkoutDatesWithBenefits?.endsAt;
  const primaryTaxBreakdown =
    subscription?.taxBreakdown || subscription?.tax_breakdown;

  return topologies.map((topology, index) => {
    const typeLabel = getPlanTypeLabel(topology.type);
    const name = topology.name || '';
    return {
      key: topology.uid || `topology-${index}`,
      title: typeLabel ? `${name} · ${typeLabel}` : name,
      displayDuration,
      endsAt,
      taxBreakdown: index === 0 ? primaryTaxBreakdown : null,
      showPrice: index === 0
    };
  });
};

/**
 * DurationInfo / payment sidebar shape from CMS iconic-pro-add-ons topologies.
 * Includes the primary Iconic Pro goal plus CMS bundle goals.
 */
export const buildIconicProCmsBundleDetails = ({
  primaryGoal,
  topologyDetails
}: {
  primaryGoal?: { uid?: string; name?: string } | null;
  topologyDetails?: CmsTopologyDetail[] | null;
}) => {
  if (!topologyDetails?.length) return null;

  const cmsTopologies = topologyDetails.map((item) => ({
    uid: item.uid,
    type: item.type,
    name: item.name
  }));

  const primaryTopology =
    primaryGoal?.uid || primaryGoal?.name
      ? [
          {
            uid: primaryGoal.uid,
            name: primaryGoal.name,
            type: subscriptionTypes.ICONIC_PRO
          }
        ]
      : [];

  return {
    topologies: [...primaryTopology, ...cmsTopologies]
  };
};

export interface ComboDisplayLineItem {
  key: string;
  type: PlanTagType;
  name: string;
  price: number;
  originalPrice?: number;
  priceLabel?: string;
}

const getTaxBreakdownTotal = (taxBreakdown?: TaxBreakdownItem[]) =>
  taxBreakdown?.find((item) => item.label === 'Total')?.amount ?? 0;

export const getPrimaryComboLineItem = (
  subscription: BundleSubscription | undefined,
  primaryGoalName: string
): ComboDisplayLineItem => ({
  key: subscription?.uid || 'primary-subscription',
  type: subscription?.type,
  name: primaryGoalName,
  price: getTaxBreakdownTotal(subscription?.tax_breakdown),
  priceLabel: 'Subscription price'
});

const getBundleLineItems = (bundle: BundleAddOn): ComboDisplayLineItem[] => {
  const childSubscriptions = (bundle.subscription?.child_subscriptions ||
    bundle.subscription?.childSubscriptions) as
    | Array<{
        uid: string;
        type: number;
        title?: string;
        goal?: { name?: string; uid?: string };
        tax_breakdown?: TaxBreakdownItem[];
        price?: string | number;
      }>
    | undefined;

  if (childSubscriptions?.length) {
    return childSubscriptions.map((child) => ({
      key: child.uid,
      type: child.type,
      name: child.goal?.name || child.title || '',
      price: getTaxBreakdownTotal(child.tax_breakdown),
      originalPrice: child.price ? Number(child.price) : undefined
    }));
  }

  const topologies = bundle.topology_details || [];

  if (topologies.length <= 1) {
    const goal = topologies[0];
    return [
      {
        key: bundle.uid || goal?.uid || 'addon-bundle',
        type: goal?.type,
        name: goal?.name || bundle.name,
        price: bundle.add_for || 0,
        originalPrice: bundle.originally_for
      }
    ];
  }

  const perAddFor = (bundle.add_for || 0) / topologies.length;
  const perOriginal = (bundle.originally_for || 0) / topologies.length;

  return topologies.map((goal, index) => ({
    key: goal.uid || `${bundle.uid}-${index}`,
    type: goal.type,
    name: goal.name,
    price: perAddFor,
    originalPrice: perOriginal
  }));
};

export const getAddedBundleLineItems = (
  addedBundle?: BundleAddOn | null
): ComboDisplayLineItem[] => {
  if (!addedBundle) return [];

  if (addedBundle.mandatoryBundles?.length) {
    return addedBundle.mandatoryBundles.flatMap(getBundleLineItems);
  }

  return getBundleLineItems(addedBundle);
};

const getSubscriptionTotal = (bundle: BundleAddOn) =>
  bundle?.subscription?.tax_breakdown?.find((item) => item.label === 'Total')
    ?.amount || 0;

export const getCheckoutBundleFromAddOns = (bundles: BundleAddOn[]) =>
  bundles.reduce(
    (best, bundle) =>
      getSubscriptionTotal(bundle) >= getSubscriptionTotal(best)
        ? bundle
        : best,
    bundles[0]
  );

export const mergeMandatoryBundleAddOns = (
  bundles?: BundleAddOn[] | null
): BundleAddOn | null => {
  if (!bundles?.length) return null;

  const validBundles = bundles.filter((bundle) => !bundle.is_referral_invalid);
  if (!validBundles.length) return null;

  if (validBundles.length === 1) {
    return { ...validBundles[0], isMandatory: true };
  }

  const checkoutBundle = getCheckoutBundleFromAddOns(validBundles);

  return {
    ...checkoutBundle,
    topology_details: validBundles.flatMap(
      (bundle) => bundle.topology_details || []
    ),
    add_for: validBundles.reduce(
      (sum, bundle) => sum + (bundle.add_for || 0),
      0
    ),
    originally_for: validBundles.reduce(
      (sum, bundle) => sum + (bundle.originally_for || 0),
      0
    ),
    name: validBundles.map((bundle) => bundle.name).join(', '),
    isMandatory: true,
    mandatoryBundles: validBundles,
    subscription: {
      ...checkoutBundle.subscription,
      bundle_saved_amount: validBundles.reduce(
        (sum, bundle) => sum + (bundle.subscription?.bundle_saved_amount || 0),
        0
      )
    }
  };
};

/**
 * Primary subscription shape BundleAddOns expects, built from checkout pricings
 * (Iconic Pro no longer uses bundle_add_ons/details for this).
 */
export const buildPrimarySubscriptionFromPlan = (
  plan?: BundleSubscription | null,
  goal?: {
    offer_activation_allowed?: boolean;
    offerActivationAllowed?: boolean;
  } | null
) => {
  if (!plan?.uid) return null;

  return {
    goal: {
      offer_activation_allowed:
        goal?.offer_activation_allowed ?? goal?.offerActivationAllowed
    },
    subscription: {
      ...plan,
      tax_breakdown:
        (plan.tax_breakdown as TaxBreakdownItem[] | undefined) ||
        (plan.taxBreakdown as TaxBreakdownItem[] | undefined),
      available_offer: plan.available_offer ?? plan.availableOffer,
      bundle_saved_amount: 0
    }
  };
};

/**
 * Iconic Pro combo — CMS topologies only (no bundle_add_ons API).
 */
export const buildIconicProComboBundle = ({
  topologyDetails,
  primarySubscription
}: {
  topologyDetails?: CmsTopologyDetail[] | null;
  primarySubscription?: {
    goal?: { offer_activation_allowed?: boolean };
    subscription?: BundleSubscription;
  } | null;
}): BundleAddOn | null => {
  if (!topologyDetails?.length) return null;

  const topologies = topologyDetails.map((item) => ({
    uid: item.uid,
    type: item.type,
    name: item.name,
    title: item.title || item.name
  }));
  const topologyName = topologies.map((item) => item.name).join(', ');
  const primarySub = primarySubscription?.subscription;
  if (!primarySub?.uid) return null;

  return {
    uid: `cms-combo-${primarySub.uid}`,
    name: topologyName,
    add_for: 0,
    originally_for: 0,
    topology_details: topologies,
    isMandatory: true,
    isCmsOnly: true,
    offer_activation_allowed:
      primarySubscription?.goal?.offer_activation_allowed,
    subscription: {
      ...primarySub,
      bundle_saved_amount: 0
    }
  };
};

export const getPlanTypeTagImage = (type: PlanTagType): string => {
  if (type === subscriptionTypes.PLUS || type === 'Plus') {
    return 'bundles/plusTagSmall.png';
  }
  if (type === subscriptionTypes.ICONIC_PRO || type === 'Iconic Pro') {
    return 'bundles/iconicProSmall.png';
  }
  return 'bundles/iconicTagSmall.png';
};

export const getPlanTagDimensions = (
  isMobile = false
): { width: number; height: number } =>
  isMobile ? { width: 90, height: 24 } : { width: 96, height: 28 };

export const isComplimentaryBundleAddOn = (
  bundle?: {
    add_for?: number;
  } | null
) => bundle?.add_for === 0;

export const getAddOnPriceLabel = (
  bundle: { add_for?: number; originally_for?: number } | null | undefined,
  currency: string
) => {
  if (isComplimentaryBundleAddOn(bundle)) {
    return `${currency}${textHelper.commas(
      Math.round(bundle?.originally_for || 0)
    )}`;
  }
  return 'Add-on price';
};
